import { MetricExplanation } from "../types/metricExplanationTypes";

/**
 * Get adjective for player style fit score
 * @param score - Player score
 * @returns "strong" for 75+, "reasonable" for 50-74, "weak" for below 50
 */
const getStyleFitAdjective = (score: number): string => {
  if (score >= 75) {
    return "strong";
  }
  if (score >= 50) {
    return "reasonable";
  }
  return "weak";
};

/**
 * Get skill level description for performance metrics
 * @param score - Player score
 * @returns "highly effective" for 75+, "capable" for 50-74, "developing" for below 50
 */
const getSkillLevel = (score: number): string => {
  if (score >= 75) {
    return "highly effective";
  }
  if (score >= 50) {
    return "capable";
  }
  return "developing";
};

/**
 * Get passing skill level description
 * @param score - Player score
 * @returns "excellent" for 75+, "reliable" for 50-74, "developing" for below 50
 */
const getPassingSkillLevel = (score: number): string => {
  if (score >= 75) {
    return "excellent";
  }
  if (score >= 50) {
    return "reliable";
  }
  return "developing";
};

const getTeamComparison = (score: number, teamAverage: number): string => {
  const difference = score - teamAverage;
  if (difference > 15) {
    return `This player scores significantly above your team's average of ${teamAverage} for players in this position, suggesting they would be a standout addition.`;
  } else if (difference > 5) {
    return `This player scores above your team's average of ${teamAverage} for players in this position, indicating potential improvement.`;
  } else if (difference > -5) {
    return `This player scores comparable to your team's average of ${teamAverage} for players in this position.`;
  }
  return `This player scores below your team's average of ${teamAverage} for players in this position, which should be considered in evaluation.`;
};

export const metricExplanations: Record<string, MetricExplanation> = {
  player_style_fit: {
    id: "player_style_fit",
    name: "Player Style Fit",
    title: "Player Style Fit",
    description: (score: number, teamAverage?: number) => {
      const adjective = getStyleFitAdjective(score);
      if (teamAverage === undefined) {
        return `With a playing style fit score of ${score}, this player is a ${adjective} match for this position's typical style.`;
      }
      return `With a playing style fit score of ${score}, this player is a ${adjective} match for this position's typical style and is comparable to your team's average of ${teamAverage}.`;
    },
    teamComparison: undefined,
    methodology:
      "This metric is an assessment of how closely a player on-pitch actions, both in location and volume, align with the ideal profiles that you have defined.",
  },
  team_style_fit: {
    id: "team_style_fit",
    name: "Team Style Fit",
    title: "Team Style Fit",
    description: (score: number) => {
      const message =
        score >= 75
          ? "is extremely well-suited to your team's tactical approach and would likely adapt seamlessly to your system"
          : score >= 50
            ? "would generally fit your team's style, though there may be some areas where adaptation is needed"
            : "may struggle to adapt to your team's tactical approach and could require significant adjustment";
      return `A Team Style Fit score of ${score} suggests the player ${message}.`;
    },
    teamComparison: undefined,
    methodology:
      "This metric compares your defined tactical style to similar systems that the player has played historically accounting for minutes played in those system.",
  },
  gemini_player_rating: {
    id: "gemini_player_rating",
    name: "Gemini Player Rating (GPR)",
    title: "Gemini Player Rating (GPR)",
    description: (score: number) => {
      return score > 100
        ? `A GPR of ${score} is truly exceptional—this player is performing at a level well above even the global elite for their position. They are likely among the very best in the world.`
        : score >= 90
          ? `A GPR of ${score} places this player in the top tier globally for their position. They are considered elite and are likely to have a major impact in any match.`
          : score >= 75
            ? `A GPR of ${score} means this player is performing at a very high level, well above average for their position. They are a key contributor and often stand out in games.`
            : score >= 50
              ? `A GPR of ${score} indicates solid, above-average performance. This player is reliable and contributes positively, though not quite at the elite level.`
              : `A GPR of ${score} suggests the player is currently performing below average for their position. There may be areas for improvement to reach a higher impact level.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a position specific, league-adjusted blend of their team's success when they are on the pitch vs off the pitch, combined with VAEP (Valuing Actions by Estimating Probabilities) - which is a framework for valuing actions that lead to goal scoring opportunities.",
    category: "Overall Performance",
  },
  carrying: {
    id: "carrying",
    name: "Ball Carrying Ability",
    title: "Ball Carrying Ability",
    description: (score: number) => {
      return `With a Ball Carrying score of ${score}, this player demonstrates ${score >= 75 ? "outstanding" : score >= 50 ? "solid" : "developing"} ability at moving the ball under control.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how often this player carries the ball and how effective those carries are at leading to goal-scoring chances.",
  },
  dribbling: {
    id: "dribbling",
    name: "Dribbling",
    title: "Dribbling",
    description: (score: number) => {
      const skillLevel = getSkillLevel(score);
      return `With a Dribbling score of ${score}, this player is ${skillLevel} at beating opponents in 1v1 situations.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how often this player takes on defenders and how effective they are at beating them.",
  },
  passing: {
    id: "passing",
    name: "Passing",
    title: "Passing",
    description: (score: number) => {
      const skillLevel = getPassingSkillLevel(score);
      return `With a Passing score of ${score}, this player is ${skillLevel} at distributing the ball and creating chances.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how often this player passes and how effective those passes are at maintaining possession and creating goal-scoring chances.",
  },
  shooting: {
    id: "shooting",
    name: "Shooting",
    title: "Shooting",
    description: (score: number) => {
      return `With a Shooting score of ${score}, this player is a ${score >= 75 ? "clinical" : score >= 50 ? "capable" : "developing"} finisher in front of goal.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how often this player shoots and how likely those shots are to result in goals based on location, timing, and defensive pressure.",
  },
  positioning: {
    id: "positioning",
    name: "Positioning",
    title: "Positioning",
    description: (score: number) => {
      return score >= 75
        ? `A goalkeeper's positioning score of ${score} indicates that the player is excellent at being in the right place at the right time to reduce the quality of shots faced.`
        : score >= 50
          ? `A goalkeeper's positioning score of ${score} indicates that the player is solid at being in the right place at the right time to reduce the quality of shots faced.`
          : `A goalkeeper's positioning score of ${score} indicates that the player is poor at being in the right place at the right time to reduce the quality of shots faced.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how well this goalkeeper's positioning reduces the quality of scoring chances faced.",
  },
  shot_stopping: {
    id: "shot_stopping",
    name: "Shot Stopping",
    title: "Shot Stopping",
    description: (score: number) => {
      return score >= 75
        ? `A goalkeeper's shot stopping score of ${score} indicates an excellent level that reflects their ability to make crucial saves, especially when facing high-quality chances.`
        : score >= 50
          ? `A goalkeeper's shot stopping score of ${score} indicates a solid level that reflects their ability to make crucial saves, especially when facing high-quality chances.`
          : `A goalkeeper's shot stopping score of ${score} indicates a poor level that reflects their ability to make crucial saves, especially when facing high-quality chances.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how well this goalkeeper performs relative to the difficulty of shots faced, comparing expected goals against actual goals conceded.",
  },
  gk_passing: {
    id: "gk_passing",
    name: "Goalkeeper Passing",
    title: "Goalkeeper Passing",
    description: (score: number) => {
      return score >= 75
        ? `A Goalkeeper Passing score of ${score} means this goalkeeper excels at distribution, accurately delivering the ball to teammates and initiating attacks from the back.`
        : score >= 50
          ? `A Goalkeeper Passing score of ${score} indicates the goalkeeper is reliable at distribution and can contribute to building play from the back.`
          : `A Goalkeeper Passing score of ${score} suggests the goalkeeper may struggle with distribution and could improve their passing accuracy or decision-making.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how effectively this goalkeeper distributes the ball.",
  },
  physical_score: {
    id: "physical_score",
    name: "Physical Score",
    title: "Physical Score",
    description: (score: number) => {
      return score >= 75
        ? `A Physical Score of ${score} indicates this player demonstrates exceptional athletic ability and work rate. Their physical output during matches consistently meets and exceeds the demanding physical requirements that coaches and scouts recognize as elite.`
        : score >= 50
          ? `A Physical Score of ${score} shows this player has solid athletic capabilities and work rate. They generally meet the physical demands expected in their position, though there may be room for improvement in some areas.`
          : `A Physical Score of ${score} suggests this player may struggle to meet the physical demands required for optimal performance. This could indicate areas where improved fitness, stamina, or physical conditioning would benefit their game.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric is a standardized assessment of how a player's athletic output and work rate during matches reflect the physical demands coaches and scouts instinctively recognize.",
  },
  epm: {
    id: "epm",
    name: "Expected Plus/Minus (EPM)",
    title: "Gemini Plus Minus",
    description: (score: number) => {
      return score >= 0.3
        ? `An EPM score of ${score.toFixed(2)} is exceptional, indicating this player consistently contributes to their team's success far above expectations. They are a game-changer who elevates team performance.`
        : score >= 0.1
          ? `An EPM score of ${score.toFixed(2)} shows this player has a positive impact on their team's performance above what would be expected. They consistently contribute to winning moments.`
          : score >= -0.1
            ? `An EPM score of ${score.toFixed(2)} indicates this player performs around expectations, neither significantly helping nor hurting their team's chances.`
            : score >= -0.3
              ? `An EPM score of ${score.toFixed(2)} suggests this player's impact on team performance is below expectations. There may be areas for improvement in their contribution to winning.`
              : `An EPM score of ${score.toFixed(2)} indicates this player significantly struggles to contribute positively to their team's performance and may need substantial development.`;
    },
    teamComparison: getTeamComparison,
    methodology:
      "This metric measures a player's total impact on their team's goal difference per 90 minutes, accounting for the quality of teammates and opponents. It combines offensive and defensive contributions into a single measure of overall effectiveness.",
  },
};

/**
 * Retrieves the explanation configuration for a specific metric
 * @param metricId - The identifier of the metric (e.g., "gpr", "passing", "shooting")
 * @returns MetricExplanation object if found, undefined if metricId is not recognized
 */
export const getMetricExplanation = (
  metricId: string
): MetricExplanation | undefined => {
  return metricExplanations[metricId];
};
